/** @jsxImportSource @emotion/react */
import Pagination from 'components/atoms/Pagination'
import { StatePill } from 'components/lab/atoms/StatePill'
import { describeMailing, formatDateTime, formatMoney } from 'components/lab/billingPatientStatements/statementFormat'
import React from 'react'
import { useGetPatientStatementRemindersQuery } from 'store/services/patientStatements'
import {
  billingStatementCycleStepLabel,
  STATEMENT_REMINDER_CHANNEL_LABEL,
  STATEMENT_REMINDER_STATUS_COLOR,
  STATEMENT_REMINDER_STATUS_LABEL,
  StatementReminderDto,
  StatementReminderStatus,
} from 'store/types'

type Props = {
  patientId: string
}

const MESSAGE_PREVIEW_LENGTH = 80

export const StatementRemindersListing: React.FC<Props> = ({ patientId }) => {
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(20)
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set())
  const { data, isFetching } = useGetPatientStatementRemindersQuery({
    patientId: Number(patientId),
    page,
    size: pageSize,
  })

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const renderStatus = (reminder: StatementReminderDto) => {
    if (reminder.mailing) {
      return <StatePill {...describeMailing(reminder.mailing)} />
    }

    const status = reminder.status as StatementReminderStatus

    return (
      <div css={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <StatePill
          color={STATEMENT_REMINDER_STATUS_COLOR[status] || '#6c757d'}
          label={STATEMENT_REMINDER_STATUS_LABEL[status] || status}
        />
        {reminder.failureReason && (
          <small className="text-danger" css={{ wordBreak: 'break-word' }}>
            {reminder.failureReason}
          </small>
        )}
      </div>
    )
  }

  const renderMessage = (reminder: StatementReminderDto) => {
    if (!reminder.message) {
      return '--'
    }

    const expanded = expandedIds.has(reminder.id)
    const long = reminder.message.length > MESSAGE_PREVIEW_LENGTH

    return (
      <div css={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {reminder.subject && <div css={{ fontWeight: 600 }}>{reminder.subject}</div>}
        {expanded || !long ? reminder.message : `${reminder.message.slice(0, MESSAGE_PREVIEW_LENGTH)}…`}
        {long && (
          <button className="btn btn-link p-0 ml-1" onClick={() => toggleExpanded(reminder.id)}>
            {expanded ? 'Less' : 'More'}
          </button>
        )}
      </div>
    )
  }

  return (
    <div>
      <div className="table-responsive" css={{ width: '100%', overflow: 'visible' }}>
        <table className="table table-sm" style={{ width: '100%', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ width: 150 }}>Date</th>
              <th style={{ width: 80 }}>Channel</th>
              <th style={{ width: 200 }}>Sent To</th>
              <th style={{ width: 190 }}>Step</th>
              <th style={{ width: 90 }}>Balance</th>
              <th style={{ width: 160 }}>Status</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {isFetching && (
              <tr>
                <td colSpan={7} className="text-center">Loading...</td>
              </tr>
            )}
            {!isFetching && !data?.items?.length && (
              <tr>
                <td colSpan={7} className="text-center">No statement reminders</td>
              </tr>
            )}
            {!isFetching && data?.items?.map((reminder: StatementReminderDto) => (
              <tr key={reminder.id}>
                <td>{formatDateTime(reminder.createdAt)}</td>
                <td>{STATEMENT_REMINDER_CHANNEL_LABEL[reminder.channel] || reminder.channel}</td>
                <td css={{ wordBreak: 'break-word' }}>{reminder.destination || '--'}</td>
                <td>{reminder.automated ? billingStatementCycleStepLabel(reminder.stepKey) : 'Sent by hand'}</td>
                <td>{reminder.balance === null ? '--' : formatMoney(reminder.balance)}</td>
                <td>{renderStatus(reminder)}</td>
                <td>{renderMessage(reminder)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        onPageChange={setPage}
        totalCount={data?.totalItems ?? 0}
        siblingCount={1}
        currentPage={page}
        pageSize={pageSize}
        className=""
        setPageSize={(size: number) => {
          setPage(0)
          setPageSize(size)
        }}
      />
    </div>
  )
}
